/* eslint-disable @typescript-eslint/naming-convention */
import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationHandlerService } from '../core/configuration-handler.service';
import { SEOService } from '../core/seo.service';
import { AssetForPortalFullAssetPage } from '../shared/interfaces/research-portal/full-asset-page/asset-for-portal-full-asset-page.interface';
import { EsploroAssetForPortalFullAssetPage } from '../shared/interfaces/research-portal/full-asset-page/esploro-asset-for-portal-full-asset-page.interface';
import { EsploroAssetDescriptionType } from '../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-sections/esploro-asset-descriptions-model';
import { EsploroAssetTitleType } from '../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-sections/esploro-asset-titles-model';
import { EsploroAssetIdentifierType } from '../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-sections/esploro-asset-identifiers-model.interface';
import { getTranslatableValue } from '../shared/interfaces/translatable';
import { getAssetDatesListByPriority } from '../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-sections/esploro-asset-dates-model.interface';

@Injectable({ providedIn: 'root' })
export class AssetSeoService {
    private seoService = inject(SEOService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private translate = inject(TranslateService);
    private document = inject<Document>(DOCUMENT);

    /** esploro asset model for asset page */
    esploroAsset: EsploroAssetForPortalFullAssetPage;
    private metaTagElements: HTMLMetaElement[] = [];
    private ogTags = {
        'og:title': () => this.title,
        'og:description': () => this.description,
        'og:url': () => this.seoService.canonicalUrl,
    };
    private ogArticleTags = {
        'og:type': () => 'article',
        'article:author': () => this.authors,
        'article:published_time': () => this.getIsoDate(this.publishedDate),
        'article:tag': () => this.esploroAsset?.researchTopics,
    };
    private ogBookTags = {
        'og:type': () => 'book',
        'book:author': () => this.authors,
        'book:isbn': () => this.isbn,
        'book:release_date': () => this.getIsoDate(this.publishedDate),
    };
    private ogMusicTags = {
        'og:type': () => 'music.song',
    };
    private ogVideoTags = {
        'og:type': () => 'video.movie',
        'video:release_date': () => this.getIsoDate(this.publishedDate),
    };
    private citationsMap: CitationNames = {};
    /** New design - URM-147453
     * All the exists information will be in the metatags.
     */
    private mainCitations: CitationNames = {
        citation_title: () => this.esploroAsset?.title,
        citation_author: () => this.authors,
        citation_doi: () => this.doi,
        citation_isbn: () => this.isbn,
        citation_publication_date: () => this.getGSDate(this.publishedDate),
        citation_online_date: () => this.getGSDate(this.onlinePublishedDate),
        citation_dissertation_institution: () =>
            this.entityModel?.etd?.degreeGrantor,
        citation_language: () =>
            this.entityModel?.general?.language
                ?.filter((lang) => !!lang)
                .join(';'),
        citation_publisher: () => this.entityModel?.publisher?.publisherName,
        citation_pdf_url: () => this.esploroAsset?.pdfFileGSUrl,
        citation_journal_title: () => this.publicationDetails?.title,
        citation_issn: () => [
            this.publicationDetails?.identifierIssn,
            this.publicationDetails?.identifierEissn,
        ],
        citation_volume: () => this.publicationDetails?.volume,
        citation_issue: () => this.publicationDetails?.issue,
        citation_firstpage: () => this.publicationDetails?.spage,
        citation_lastpage: () => this.publicationDetails?.epage,
        citation_conference_title: () => this.entityModel?.conference?.name,
        citation_date_submitted: () => this.submittedDate,
    };
    /** citations for BookChapter only. includes global citations */
    private bookChapter: CitationNames = {
        ...this.mainCitations,
        citation_journal_title: () => '', // Override global citation
        citation_inbook_title: () => this.publicationDetails?.title,
    };
    /** citations for technicalDocumentation only. includes global citations */
    private technicalDocumentation: CitationNames = {
        ...this.mainCitations,
        citation_technical_report_institution: () =>
            this.technicalReportInstitution(),
    };
    /** citations for all ETDs. includes global citations */
    private etdCitations: CitationNames = {
        ...this.technicalDocumentation,
    };

    get entityModel() {
        return this.esploroAsset?.entityModel;
    }

    get publicationDetails() {
        return this.entityModel?.publicationDetails;
    }

    /** There always should be a description */
    get description(): string {
        if (!this.esploroAsset.description) {
            const translatedResourceType = this.translate.instant(
                `portal.${this.esploroAsset?.resourceTypeValue}`,
            );
            return `${this.title} - ${translatedResourceType}`;
        }
        return this.esploroAsset.description;
    }

    /**
     * * URM-120675
     * citation_publication_date - create ONLY from date.published
     */
    private get publishedDate(): string {
        return this.entityModel?.dates?.assetDates?.DATE_PUBLISHED
            ?.datesList[0];
    }

    /**
     * * URM-120675
     * citation_online_date - first take date.epublished
     *  if there is not date.epublished AND no date.published then take any other date field - in order of importance (based on date hierarchy)
     */
    private get onlinePublishedDate(): string {
        let defaultDate = '';
        if (!this.publishedDate) {
            defaultDate = Object.values(
                this.entityModel?.dates?.assetDates,
            )?.filter((assetDate) => assetDate?.datesList[0] != null)[0]
                ?.datesList[0];
        }
        return (
            this.entityModel?.dates?.assetDates?.DATE_EPUBLISHED
                ?.datesList[0] || defaultDate
        );
    }

    /**
     * * (URM-193259), URM-204693
     * citation_date_submitted - Take By below priority (if first empty or not exists):
     * - Deposit policy accepted date.
     * - Date approval.
     * - Created date.
     * !Note: It's not part of GS
     */
    private get submittedDate(): string {
        let date =
            this.entityModel.depositPolicy?.acceptedDate ||
            this.entityModel.statusUpdatesModel?.statusUpdates?.find(
                (status) => status.updatedTo === 'APPROVED',
            )?.updateDate ||
            this.esploroAsset.entity.createDate;
        // Assume that in this format all the properties are not null.
        // The month starts from 0 for January
        return date
            ? this.getIsoDateFormat(
                  `${date.year}`,
                  `${date.month + 1}`,
                  `${date.dayOfMonth}`,
              )
            : '';
    }

    private get isbn(): string[] {
        const isbn = this.entityModel.identifiers?.assetIdentifiers?.ISBN || [];
        const eisbn =
            this.entityModel.identifiers?.assetIdentifiers?.EISBN || [];
        return [...isbn, ...eisbn];
    }

    private get authors(): string[] {
        return this.esploroAsset?.creators?.map(
            (author) => author?.displayName,
        );
    }

    private get title(): string {
        return `${
            this.esploroAsset.title
        } - ${this.configurationHandlerService.mainInstitutionName()}`;
    }

    private get doi(): string {
        const doi = this.entityModel?.identifiers?.assetIdentifiers?.DOI;
        return doi?.length > 0 ? doi[0] : '';
    }

    getCitation(key: string): assetobject {
        const citation = this.citationsMap[key];
        return citation();
    }

    /**
     * Formats date for Google Scholar
     *
     * @param dateString date in the format `'yyyyMMdd' | 'yyyyMM' | 'yyyy'`
     * @return date in the format `'yyyy/M/dd' | 'yyyy'` (or empty string if recieved format incorrect)
     */
    getGSDate(dateString: string): string {
        if (!dateString || dateString.length < 'yyyy'.length) {
            return '';
        }

        const year = dateString.substr(0, 4);
        if (dateString.length === 'yyyyMMdd'.length) {
            const monthMFormat = parseInt(dateString.substr(4, 2));
            const day = dateString.substr(6, 2);

            return `${year}/${monthMFormat}/${day}`;
        }
        return year;
    }

    /**
     * Formats date according to {@link https://en.wikipedia.org/wiki/ISO_8601#Dates|ISO 8601}
     * for use in Open Graph tags. Assumes input string is relatively valid.
     *
     * @param dateString date in the format `'yyyyMMdd' | 'yyyyMM' | 'yyyy'`
     * @return date in the format `'yyyy-MM-dd' | 'yyyy-MM' |'yyyy'`
     */
    getIsoDate(dateString: string): string {
        if (!dateString || dateString.length < 'yyyy'.length) {
            return '';
        }

        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);

        return this.getIsoDateFormat(year, month, day);
    }

    private getIsoDateFormat(year: string, month: string, day: string) {
        return (
            year +
            (month ? '-' + month.padStart(2, '0') : '') +
            (day ? '-' + day.padStart(2, '0') : '')
        );
    }

    addGoogleMetaTags(model: AssetForPortalFullAssetPage) {
        this.initModel(model);
        this.changeDocumentTitle();
        this.updateDocuemntDescription();
        this.seoService.createLinkForCanonicalURL();
        this.updateGoogleScholarTags();
        this.updateGoogleOGTags();
        this.addSchemaOrgScript();
    }

    removeGoogleMetaTags() {
        while (this.metaTagElements.length > 0) {
            this.seoService.removeMetaTagElement(this.metaTagElements.pop());
        }
        // TODO: og meta tags should be always in DOM (primo code - index.html).
    }

    initModel({
        esploroAssetForPortalFullAssetPage,
    }: AssetForPortalFullAssetPage) {
        this.esploroAsset = esploroAssetForPortalFullAssetPage;
    }

    addSchemaOrgScript() {
        let json;
        if (this.isDataset()) {
            json = this.prepareDatasetJson();
        }
        if (json) {
            this.seoService.createSchemaDotOrgScript(json);
        }
    }

    private technicalReportInstitution(): assetobject {
        return (
            this.entityModel?.publisher?.publisherName ??
            this.configurationHandlerService.mainInstitutionName()
        );
    }

    private isArticle = () =>
        [
            'publication.bookReview',
            'postedContent.acceptedManuscript',
            'publication.conferenceProceeding',
            'publication.journalArticle',
        ].includes(this.esploroAsset?.resourceTypeValue);

    private isBook = () =>
        [
            'publication.book',
            'publication.editedBook',
            'publication.translation',
            'creativeWork.fiction',
        ].includes(this.esploroAsset?.resourceTypeValue);

    private isMusic = () =>
        [
            'creativeWork.musicalScore',
            'creativeWork.musicalComposition',
        ].includes(this.esploroAsset?.resourceTypeValue);

    private isVideo = () =>
        ['creativeWork.film'].includes(this.esploroAsset?.resourceTypeValue);

    private isBookChapter = () =>
        ['publication.bookChapter'].includes(
            this.esploroAsset?.resourceTypeValue,
        );

    private isTechnicalDocumentation = () =>
        ['publication.technicalDocumentation'].includes(
            this.esploroAsset?.resourceTypeValue,
        );

    private isDataset = () =>
        ['dataset.dataset'].includes(this.esploroAsset?.resourceTypeValue);

    private isETD = () =>
        [
            'etd',
            'etd.undergraduate',
            'etd.graduate',
            'etd.doctoral',
            'etdex',
            'etdexternal.undergraduate_external',
            'etdexternal.doctoral_external',
            'etdexternal.graduate_external',
        ].includes(this.esploroAsset?.resourceTypeValue);

    changeDocumentTitle() {
        this.seoService.updateTitle(this.title);
    }

    private updateDocuemntDescription() {
        this.seoService.updateDescription(this.description);
    }

    private updateGoogleScholarTags() {
        this.citationsMap = this.initCitationMap();
        for (const citationName in this.citationsMap) {
            try {
                const citationContent = this.getCitation(citationName);
                this.addMetaTags(citationName, citationContent);
            } catch (e) {
                console.error(e);
            }
        }
    }

    private updateGoogleOGTags() {
        this.parsePropTagObject(this.ogTags);

        if (this.isArticle()) {
            this.parsePropTagObject(this.ogArticleTags);
        }
        if (this.isBook()) {
            this.parsePropTagObject(this.ogBookTags);
        }
        if (this.isMusic()) {
            this.parsePropTagObject(this.ogMusicTags);
        }
        if (this.isVideo()) {
            this.parsePropTagObject(this.ogVideoTags);
        }
    }

    private parsePropTagObject(tagObj) {
        for (const property in tagObj) {
            const propertyValue = tagObj[property]();
            if (Array.isArray(propertyValue)) {
                // for OG tags, we should put multiple tags for multiple entries
                // therefore, we need to remove old ones first
                this.seoService.removeMetaTag(`property="${property}"`);
                for (const item of propertyValue) {
                    // and then add in all the new ones
                    this.seoService.addPropertyMetaTag(property, item);
                }
            } else {
                // if it's not an array, we handle it as a single tag
                this.seoService.updatePropertyMetaTag(
                    property,
                    tagObj[property](),
                );
            }
        }
    }

    private initCitationMap() {
        const citation = this.isBookChapter()
            ? this.bookChapter
            : this.isTechnicalDocumentation()
              ? this.technicalDocumentation
              : this.isETD()
                ? this.etdCitations
                : this.mainCitations;
        return citation;
    }

    private addMetaTags(name: string, value: assetobject) {
        if (typeof value === 'string') {
            const metaTag = this.seoService.updateMetaTag(name, value);
            this.metaTagElements.push(metaTag);
        } else {
            value?.forEach((content) => {
                const metaTag = this.seoService.addMetaTag(name, content);
                this.metaTagElements.push(metaTag);
            });
        }
    }

    private prepareDatasetJson() {
        const currLang = this.translate.currentLang;
        const json = {
            '@context': 'https://schema.org/',
            '@type': 'Dataset',
            name: this.esploroAsset.title,
        };

        const assetDesc =
            this.esploroAsset.entityModel?.descriptions?.assetDescriptions;
        if (assetDesc?.[EsploroAssetDescriptionType.ABSTRACT]?.descriptions) {
            json['description'] = getTranslatableValue(
                assetDesc[EsploroAssetDescriptionType.ABSTRACT].descriptions,
                currLang,
            ).join(' ');
        } else {
            json['description'] = Object.keys(assetDesc)
                .map((type) =>
                    getTranslatableValue(
                        assetDesc[type].descriptions,
                        currLang,
                    ).join(' '),
                )
                .join(' ');
        }
        if (json['description']?.length > 5000) {
            json['description'] = json['description'].slice(0, 4999) + '…';
        } else if (json['description']?.length < 50) {
            return null;
        }

        json['alternateName'] = getTranslatableValue(
            this.esploroAsset.entityModel?.titles?.titles?.[
                EsploroAssetTitleType.ALTERNATIVE
            ]?.titlesList,
            currLang,
        );

        json['creator'] = this.esploroAsset.creators?.map((creator) => {
            const obj = { name: creator.displayName };
            if (
                creator.source === 'groupAuthor' &&
                creator.role === 'Institution'
            ) {
                obj['@type'] = 'Organization';
            } else {
                obj['@type'] = 'Person';
                if (creator.orcid) {
                    obj['sameAs'] = 'https://orcid.org/' + creator.orcid;
                }
            }

            return obj;
        });

        const citationRelations = this.esploroAsset.relations?.filter((rel) =>
            rel.resourceType.startsWith('publication'),
        );
        if (citationRelations.length > 0) {
            json['citation'] = (
                citationRelations.find((rel) =>
                    rel.relatedAssetURL.startsWith('https://doi.org'),
                ) ?? citationRelations[0]
            )?.relatedAssetURL;
        }

        json['funder'] = this.esploroAsset.grants?.map((grant) => ({
            '@type': 'Organization',
            name: grant.funderName,
        }));

        const relDatasets = this.esploroAsset.relations?.filter(
            (rel) => rel.resourceType === 'dataset.dataset',
        );
        json['isPartOf'] = relDatasets.find(
            (rel) => rel.relationType === 'ispartof',
        )?.relatedAssetURL;
        json['hasPart'] = relDatasets.find(
            (rel) => rel.relationType === 'haspart',
        )?.relatedAssetURL;

        const ids = [];
        for (const key in this.esploroAsset.entityModel?.identifiers
            ?.assetIdentifiers) {
            const value =
                this.esploroAsset.entityModel.identifiers.assetIdentifiers[key];
            if (key === EsploroAssetIdentifierType.DOI) {
                ids.push(...value.map((id) => 'https://doi.org/' + id));
            } else if (key === EsploroAssetIdentifierType.ARXIV) {
                ids.push(...value.map((id) => 'https://arxiv.org/abs/' + id));
            } else if (key === EsploroAssetIdentifierType.WOS) {
                ids.push(
                    ...value.map((id) =>
                        'https://www.webofscience.com/wos/woscc/full-record/' +
                        id.toLowerCase().startsWith('wos:')
                            ? id
                            : 'WOS:' + id,
                    ),
                );
            } else if (
                !key.toString().startsWith('ADDITIONAL') &&
                key !== EsploroAssetIdentifierType.PMID &&
                key !== EsploroAssetIdentifierType.PMCID
            ) {
                ids.push(...value);
            }
        }

        if (ids.length > 0) {
            json['identifiers'] = ids;
        }

        json['isAccessibleForFree'] =
            this.esploroAsset.openAccessIndicator === 'Yes';

        const keywords = [
            ...this.esploroAsset.researchKeywords,
            ...this.esploroAsset.researchTopics,
        ];
        if (keywords.length > 0) {
            json['keywords'] = keywords;
        }

        json['license'] = this.esploroAsset?.licenseDetails?.licenseURL;

        const point =
            this.esploroAsset.entityModel?.geoLocation?.geoLocationPoints?.[0];
        const box =
            this.esploroAsset.entityModel?.geoLocation?.geoLocationBoxs?.[0];
        const address =
            this.esploroAsset.entityModel?.geoLocation?.geoLocationAddress?.[0];
        if (point) {
            json['spatialCoverage'] = {
                '@type': 'Place',
                geo: {
                    '@type': 'GeoCoordinates',
                    latitude: point.latitude,
                    longitude: point.longitude,
                },
            };
        } else if (box) {
            json['spatialCoverage'] = {
                '@type': 'Place',
                geo: {
                    '@type': 'GeoShape',
                    box: [
                        box.southBoundLongitude,
                        box.westBoundLongitude,
                        box.northBoundLongitude,
                        box.eastBoundLongitude,
                    ].join(' '),
                },
            };
        } else if (address) {
            json['spatialCoverage'] = address.address;
        }

        const dates = getAssetDatesListByPriority(
            this.esploroAsset.entityModel?.dates?.assetDates,
        );
        if (dates?.length > 0) {
            json['temporalCoverage'] = dates[0]
                .split(' - ')
                .map((date) => {
                    const y = date.slice(0, 4);
                    const m = date.slice(4, 6);
                    const d = date.slice(6);

                    if (d !== '') {
                        return [y, m, d].join('-');
                    } else if (m !== '') {
                        return [y, m].join('-');
                    } else {
                        return y;
                    }
                })
                .join('/');
        }

        json['url'] = this.seoService.canonicalUrl;

        if (assetDesc?.[EsploroAssetDescriptionType.METHODS]?.descriptions) {
            json['measurementTechnique'] = getTranslatableValue(
                assetDesc?.[EsploroAssetDescriptionType.METHODS]?.descriptions,
                currLang,
            ).join(', ');
        }

        if (this.esploroAsset.entityModel?.publisher?.publisherName) {
            json['publisher'] = {
                '@type': 'Organization',
                name: this.esploroAsset.entityModel?.publisher?.publisherName,
            };
        }

        if (this.esploroAsset.entityModel?.general?.size?.length > 0) {
            json['size'] =
                this.esploroAsset.entityModel?.general?.size?.join(' ');
        }

        if (this.esploroAsset.filesMetadata?.length > 0) {
            json['distribution'] = this.esploroAsset.filesMetadata.map(
                (file) => ({
                    '@type': 'DataDownload',
                    encodingFormat: file.esploroFileModel?.fileExtension,
                    contentUrl: file.viewerLink,
                    contentSize: file.esploroFileModel.fileSizeBytes + ' bytes',
                }),
            );
        }
        return json;
    }
}

interface CitationNames {
    [key: string]: () => assetobject;
}

type assetobject = string | string[];
